import { type OAuthConfig } from '@/models';
import { type Paths } from '@/router';
import {
  type EnterpriseContext,
  i18n,
  localStorageService,
  sessionStorageService,
} from '@nosinovacao/nosid-mfe-common';
import { UserManager, WebStorageStateStore } from 'oidc-client';

const logoutRedirectUri: Paths = '/home';
const loginRedirectUri: Paths = '/login-complete';
const silentRedirectUri: Paths = '/silent-token-renew';

const userAppTokenKey = 'user_app_token';
const loginFinalRedirectUrlKey = 'login_final_redirect_url';
const loginHintStorageKey = 'login_hint';

export class Auth {
  private userManager: UserManager = null as unknown as UserManager;
  private config: OAuthConfig = null as unknown as OAuthConfig;
  private initialized = false;
  init(config: OAuthConfig, context: EnterpriseContext | undefined) {
    if (this.initialized) return;
    //? set config with context overrides
    this.config = {
      ...config,
      ...config.ConfigForContext?.[context ?? ''],
    };
    //? get token from query if present
    const paarms = new URLSearchParams(globalThis.location.search);
    const token = paarms.get('token');
    if (token) {
      sessionStorageService.setString(userAppTokenKey, token);
    }
    //? save login_hint if present
    const loginHint = paarms.get('login_hint')?.replaceAll(' ', '+') ?? '';
    if (loginHint) {
      sessionStorageService.setString(loginHintStorageKey, loginHint);
    }
    //? initialize user manager
    this.userManager = new UserManager({
      authority: this.config.IdentityServerUrl,
      client_id: this.config.ClientId,
      response_type: this.config.ResponseType,
      scope: this.config.Scope,
      userStore: new WebStorageStateStore({ store: localStorage }),
      automaticSilentRenew: true,
      redirect_uri: `${globalThis.location.origin}${loginRedirectUri}`,
      post_logout_redirect_uri: `${globalThis.location.origin}${logoutRedirectUri}`,
      silent_redirect_uri: `${globalThis.location.origin}${silentRedirectUri}`,
    });
    this.initialized = true;
  }

  async isLoggedIn(): Promise<boolean> {
    const token = this.getUserTokenFromStorage();
    if (token) {
      return true;
    }
    return await this.userManager
      .getUser()
      .then((user) => !!user && !user.expired)
      .catch(() => false);
  }

  async processLoginRedirect(): Promise<string> {
    await this.userManager.signinRedirectCallback().catch(async () => {
      const isLoggedIn = await this.isLoggedIn();
      if (!isLoggedIn) await this.login();
    });
    let redirectPath = '/';
    if (localStorageService.checkItem(loginFinalRedirectUrlKey)) {
      redirectPath = localStorageService.getString(loginFinalRedirectUrlKey) ?? '';
      localStorageService.remove(loginFinalRedirectUrlKey);
    }
    return redirectPath;
  }

  async processSilentRedirect(): Promise<boolean> {
    return await this.userManager.signinSilentCallback().then(() => true);
  }

  async login(): Promise<void> {
    this.saveCurrentUrl();
    await this.userManager.signinRedirect({
      extraQueryParams: {
        login_hint: sessionStorageService.getString(loginHintStorageKey) ?? '',
        lang: i18n.getSnapshot().language,
      },
    });
  }

  async logout(saveCurrentUrl = true, deleteAccount: boolean = false): Promise<void> {
    sessionStorageService.remove(userAppTokenKey);
    sessionStorageService.remove(loginHintStorageKey);

    if (saveCurrentUrl) this.saveCurrentUrl(true);
    await this.userManager.signoutRedirect({
      extraQueryParams: {
        client_id: this.config.ClientId,
        lang: i18n.getSnapshot().language,
        delete_account: deleteAccount,
      },
    });
  }

  async getToken(): Promise<string | null | undefined> {
    const token = this.getUserTokenFromStorage();
    if (token?.length) return token;

    return await this.userManager
      .getUser()
      .then((user) => user?.access_token)
      .catch(() => null);
  }

  async getUserId(): Promise<string | null> {
    return await this.userManager
      .getUser()
      .then((user) => user?.profile.sub?.split('|')[1] ?? null)
      .catch(() => null);
  }

  private getUserTokenFromStorage(): string | null {
    return sessionStorageService.getString(userAppTokenKey);
  }

  private saveCurrentUrl(force: boolean = false): void {
    if (force || !localStorageService.checkItem(loginFinalRedirectUrlKey)) {
      const params = new URLSearchParams(globalThis.location.search);
      params.delete('token');
      const search = params.toString().length ? `?${params.toString()}` : '';
      localStorageService.setString(
        loginFinalRedirectUrlKey,
        globalThis.location.pathname + search,
      );
    }
  }
}

const globalForAuth = globalThis as unknown as {
  auth: Auth;
};

export const auth = globalForAuth.auth ?? new Auth();
globalForAuth.auth = auth;
